import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
// @mui
import {
  Card,
  Stack,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Container,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import MinUsersSelect from "src/components/MinUsersSelect";
import MaxUsersSelect from "src/components/MaxUsersSelect";
import { useParams, useNavigate } from "react-router-dom";
import LeaderboardTable from "src/components/LeaderboardTable";
import Iconify from "../../components/iconify";
import { get, put, post } from "src/utils/networkUtils";
import {
  handleFileUpload,
  createGuaranteeHandlers,
  createPayoutFormatChangeHandler,
  getChallengeTypeLabel,
} from "src/utils/challengeUtils";
import GuaranteeSettings from "src/components/GuaranteeSettings";
import PayoutFormatSelect from "src/components/PayoutFormatSelect";
const dateFormat = "YYYY-MM-DD HH:mm:ss";

export default function ChallengePage() {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [teams, setTeams] = useState([]);
  const [stages, setStages] = useState([]);
  const [guaranteeSettingsOpen, setGuaranteeSettingsOpen] = useState(false);
  const [guaranteeValues, setGuaranteeValues] = useState({});
  const [loading, setLoading] = useState(false);

  const mode = "edit";
  const redirectPath = "/dashboard/challenges";
  const isEditMode = mode === "edit";
  const isPublicChallenge = formData.challengeType === 1;

  useEffect(() => {
    const fetchTeams = async () => {
      const response = await get("/admin/teams?competition_id=1");
      if (response.teams.length) {
        setTeams(response.teams);
      }
    };

    const fetchStages = async () => {
      try {
        const response = await get("/admin/stages");
        if (response.stages && response.stages.length) {
          setStages(response.stages);
        }
      } catch (error) {
        console.error("Error fetching stages:", error);
      }
    };

    fetchTeams();
    fetchStages();
  }, []);

  useEffect(() => {
    const fetchChallengeDetail = async () => {
      try {
        setLoading(true);
        if (challengeId) {
          const response = await get(`/admin/challenge-detail/${challengeId}`);
          if (response.challenge) {
            setFormData(response.challenge);
          }
        } else if (mode === "create") {
          // Set default values for create mode
          setFormData({
            challengeType: 0, // Default to club challenge (0), can be changed later if needed
            minXpLevel: 0,
            prizePoolPercentage: 90,
            minUsers: 2,
            maxUsers: 0,
            entryFee: 100,
          });
        }
      } catch (error) {
        console.error("Error fetching challenge detail:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchChallengeDetail();
  }, [challengeId, mode]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateChange = (value, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: moment(value).seconds(0).format(dateFormat),
    }));
  };

  const handleSave = async () => {

    const apiPayload = {
      challengeName: formData.challengeName,
      challengeType: formData.challengeType,
      minXpLevel: formData.minXpLevel,
      payoutFormat: formData.payoutFormat,
      prizePoolPercentage: formData.prizePoolPercentage,
      maxUsers: formData.maxUsers,
      minUsers: formData.minUsers,
      entryFee: formData.entryFee,
      iconUrl: formData.iconUrl || null,
      bannerUrl: formData.bannerUrl || null,
      teamId: formData.teamId,
      stageId: formData.stageId || null,
      publishDate: formData.publishDate || null,
      guaranteeValues:
        formData.guaranteeValues &&
          Object.keys(formData.guaranteeValues).length > 0
          ? formData.guaranteeValues
          : null,
    };

    try {
      let resp;
      if (mode === 'create') {
        resp = await post("admin/challenges", apiPayload);
      } else {
        resp = await put(`admin/challenges/${formData.challengeId}`, apiPayload);
      }

      if (resp.success) {
        navigate(redirectPath);
      }
    } catch (error) {
      console.error("Error saving challenge:", error);
    }
  };

  const handleCancel = () => {
    navigate(redirectPath);
  };

  const handleFileUploadWrapper = async (type, event) => {
    return handleFileUpload(type, event, setFormData);
  };

  // Create handlers state
  const [guaranteeHandlers, setGuaranteeHandlers] = useState(null);
  const [payoutFormatChangeHandler, setPayoutFormatChangeHandler] = useState(null);

  // Create handlers when formData is available
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      const handlers = createGuaranteeHandlers(
        formData,
        setFormData,
        guaranteeValues,
        setGuaranteeValues,
        setGuaranteeSettingsOpen
      );
      setGuaranteeHandlers(handlers);

      const payoutHandler = createPayoutFormatChangeHandler(
        formData,
        setFormData,
        setGuaranteeValues,
        setGuaranteeSettingsOpen,
        isEditMode && (!formData?.status || !["open", "live", "finished"].includes(formData.status))
      );
      setPayoutFormatChangeHandler(() => payoutHandler);
    }
  }, [formData, guaranteeValues, isEditMode]);

  // Extract handlers with null safety
  const handleGuaranteeSettingsSave = guaranteeHandlers?.handleGuaranteeSettingsSave || (() => { });
  const handleGuaranteeSettingsCancel = guaranteeHandlers?.handleGuaranteeSettingsCancel || (() => { });
  const handleGuaranteeValueChange = guaranteeHandlers?.handleGuaranteeValueChange || (() => { });
  const handlePayoutFormatChange = payoutFormatChangeHandler || (() => { });

  const isFieldDisabled = () => {
    if (
      formData.status &&
      ["open", "live", "finished", "void"].includes(formData.status)
    ) {
      return true;
    }
    return false;
  };



  const getPageTitle = () => {
    if (mode === 'create') {
      return `Create ${isPublicChallenge ? 'Public' : 'Club'} Challenge`;
    }
    return `Challenge ${challengeId} (${isPublicChallenge ? 'Public' : 'Club'} - Edit Details`;
  };





  const renderActionButtons = () => {
    return (
      <Stack direction="row" spacing={2}>
        <Button
          variant="outlined"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
        >
          {mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <Helmet>
        <title>View Club Challenge</title>
      </Helmet>

      <Container>
        {loading ? (
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Header Section */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={5}
            >
              <Typography variant="h4" gutterBottom>
                {getPageTitle()}
              </Typography>
              {renderActionButtons()}
            </Stack>

            {/* Main Form */}
            <Card sx={{ p: 3 }}>
              {/* Form Fields in Two Columns */}
              <Box sx={{ display: "flex", gap: 4 }}>
                {/* Left Column */}
                <Box sx={{ flex: 1 }}>
                  <Stack spacing={3}>
                    {/* Challenge Type Field - Always first, disabled */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Challenge Type:
                      </Typography>
                      <TextField
                        value={getChallengeTypeLabel(formData.challengeType)}
                        disabled={true}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 5,
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      />
                    </Box>

                    {/* Name Field */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Name:
                      </Typography>
                      <TextField
                        value={formData.challengeName || ""}
                        onChange={(e) =>
                          handleInputChange("challengeName", e.target.value)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 5,
                            backgroundColor: "white",
                          },
                        }}
                      />
                    </Box>

                    {/* Select Team Field - Hidden for Public Challenges */}
                    {!isPublicChallenge && (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Select Team:
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={formData.teamId}
                            onChange={(e) =>
                              handleInputChange("teamId", e.target.value)
                            }
                            disabled={isFieldDisabled()}
                            sx={{
                              borderRadius: 5,
                              backgroundColor: "white",
                              "& .MuiSelect-icon": {
                                color: "#667eea",
                              },
                            }}
                          >
                            {teams.map((team) => (
                              <MenuItem key={team.scoreableId} value={team.externalRef}>
                                {team.displayName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          Challenge will run every day that Team is playing
                        </Typography>
                      </Box>
                    )}

                    {/* Stage Field */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Stage:
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={formData.stageId || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "stageId",
                              e.target.value ? parseInt(e.target.value) : null
                            )
                          }
                          disabled={isFieldDisabled()}
                          sx={{
                            borderRadius: 5,
                            backgroundColor: "white",
                            "& .MuiSelect-icon": {
                              color: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>None (Auto-detect from dates)</em>
                          </MenuItem>
                          {stages.map((stage) => (
                            <MenuItem key={stage.stageId} value={stage.stageId}>
                              {stage.stageName} (
                              {moment(stage.startDate).format("MMM DD")} -{" "}
                              {moment(stage.endDate).format("MMM DD, YYYY")})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Min XP Level Field */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Min XP Level:
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={formData.minXpLevel}
                          onChange={(e) =>
                            handleInputChange("minXpLevel", parseInt(e.target.value))
                          }
                          disabled={isFieldDisabled()}
                          sx={{
                            borderRadius: 5,
                            backgroundColor: "white",
                            "& .MuiSelect-icon": {
                              color: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value={0}>Any</MenuItem>
                          {Array.from({ length: 20 }, (_, i) => (
                            <MenuItem key={i + 1} value={i + 1}>
                              Level {i + 1}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box>
                      <MinUsersSelect
                        disabled={isFieldDisabled()}
                        value={formData.minUsers}
                        onChange={(value) => handleInputChange("minUsers", value)}
                        fullWidth
                        editmode={isEditMode}
                      />
                    </Box>
                  </Stack>
                </Box>

                {/* Right Column */}
                <Box sx={{ flex: 1 }}>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Publish Date (optional):
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                          label="Publish Date"
                          value={formData.publishDate ? moment(formData.publishDate, dateFormat) : null}
                          onChange={(value) => handleDateChange(value, "publishDate")}
                          disabled={isFieldDisabled()}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 5,
                                  backgroundColor: "white",
                                },
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        If not set, challenge will be published immediately
                      </Typography>
                    </Box>
                    {/* Entry Fee Field */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Coin entry:
                      </Typography>
                      <TextField
                        value={formData.entryFee}
                        onChange={(e) =>
                          handleInputChange("entryFee", parseInt(e.target.value) || 0)
                        }
                        disabled={isFieldDisabled()}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 5,
                            backgroundColor: "white",
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <img
                              src="/assets/icons/ufcoin.svg"
                              alt="dollar"
                              style={{ width: 20, height: 20, marginRight: 20 }}
                            />
                          ),
                        }}
                      />
                    </Box>

                    {/* Payout Format Field */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Payout Format:
                      </Typography>
                      <PayoutFormatSelect
                        value={formData.payoutFormat}
                        onChange={handlePayoutFormatChange}
                        disabled={isFieldDisabled()}
                        guaranteeValues={formData.guaranteeValues}
                      />
                    </Box>

                    {/* Prize Pool Percentage Field */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Prize Pool:
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={formData.prizePoolPercentage}
                          onChange={(e) =>
                            handleInputChange(
                              "prizePoolPercentage",
                              parseInt(e.target.value)
                            )
                          }
                          disabled={isFieldDisabled()}
                          sx={{
                            borderRadius: 5,
                            backgroundColor: "white",
                            "& .MuiSelect-icon": {
                              color: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value={100}>100% (no commission)</MenuItem>
                          <MenuItem value={95}>95%</MenuItem>
                          <MenuItem value={92}>92%</MenuItem>
                          <MenuItem value={90}>90%</MenuItem>
                          <MenuItem value={85}>85%</MenuItem>
                          <MenuItem value={80}>80%</MenuItem>
                          <MenuItem value={20}>20%</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Max Users Field */}
                    <Box>
                      <MaxUsersSelect
                        value={formData.maxUsers}
                        onChange={(value) => handleInputChange("maxUsers", value)}
                        disabled={isFieldDisabled()}
                        fullWidth
                        editmode={isEditMode}
                      />
                    </Box>
                  </Stack>
                </Box>
              </Box>

              {/* Image Upload Sections */}
              <Box sx={{ display: "flex", gap: 4, mt: 4, flexDirection: "column" }}>
                {/* Icon Upload */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, width: "150px" }}
                  >
                    Icon: (optional)
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Box
                      sx={{
                        width: 212,
                        height: 210,
                        borderRadius: 2,
                        border: "2px dashed #ddd",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                        overflow: "hidden",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {formData.iconUrl ? (
                        <img
                          src={formData.iconUrl}
                          alt="icon 1"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Iconify
                          icon="eva:image-outline"
                          sx={{
                            color: "#ccc",
                            width: 100,
                            height: 100,
                          }}
                        />
                      )}
                    </Box>
                    {!isFieldDisabled() && (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUploadWrapper("icon", e)}
                          style={{ display: "none" }}
                          id="icon-upload"
                        />
                        <label style={{ textAlign: "center" }} htmlFor="icon-upload">
                          <Typography
                            component="span"
                            sx={{
                              textAlign: "center",
                              color: "#667eea",
                              cursor: "pointer",
                              textDecoration: "underline",
                              "&:hover": {
                                color: "#5a6fd8",
                              },
                            }}
                          >
                            Upload
                          </Typography>
                        </label>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Banner Upload */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, width: "150px" }}
                  >
                    Banner: (optional)
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        height: 180,
                        width: 660,
                        borderRadius: 2,
                        border: "2px dashed #ddd",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                        overflow: "hidden",
                        backgroundColor: "#f8f9fa",
                        position: "relative",
                      }}
                    >
                      {formData.bannerUrl ? (
                        <img
                          src={formData.bannerUrl}
                          alt="Banner 1"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Iconify
                          icon="eva:image-outline"
                          sx={{ width: 120, height: 120, color: "#ccc" }}
                        />
                      )}
                    </Box>
                    {!isFieldDisabled() && (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUploadWrapper("banner1", e)}
                          style={{ display: "none" }}
                          id="banner1-upload"
                        />
                        <label htmlFor="banner1-upload">
                          <Typography
                            component="span"
                            sx={{
                              color: "#667eea",
                              cursor: "pointer",
                              textDecoration: "underline",
                              "&:hover": {
                                color: "#5a6fd8",
                              },
                            }}
                          >
                            Upload
                          </Typography>
                        </label>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Leaderboard for View Mode */}
              {isEditMode && formData.status !== "upcoming" && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                    Leaderboard
                  </Typography>
                  <LeaderboardTable formaData={formData} />
                </Box>
              )}
            </Card>

            {/* Guarantee Settings Card */}
            <GuaranteeSettings
              formData={formData}
              guaranteeValues={guaranteeValues}
              guaranteeSettingsOpen={guaranteeSettingsOpen}
              onGuaranteeValueChange={handleGuaranteeValueChange}
              onSave={handleGuaranteeSettingsSave}
              onCancel={handleGuaranteeSettingsCancel}
            />
          </>
        )}
      </Container>
    </>
  );
} 